import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import Breadcrumb from '../Breadcrumb';

interface DatasetManagementProps {
  projectId: string;
}

const DatasetManagement: React.FC<DatasetManagementProps> = ({ projectId }) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [replacementMode, setReplacementMode] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);
  const [targetLabel, setTargetLabel] = useState<string>('');
  const [columnMapping, setColumnMapping] = useState<{[key: string]: string}>({});
  const [showMapping, setShowMapping] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [summary, setSummary] = useState<any>(null);
  const [missingValues, setMissingValues] = useState<any>(null);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [selectedMissingFeature, setSelectedMissingFeature] = useState<string | null>(null);
  const [showFillModal, setShowFillModal] = useState(false);
  const [fillAnalysis, setFillAnalysis] = useState<any>(null);
  const [isPredefined, setIsPredefined] = useState(false);
  const [predefinedValues, setPredefinedValues] = useState<string[]>([]);
  const [predictions, setPredictions] = useState<any[]>([]);
  const [loadingPredictions, setLoadingPredictions] = useState(false);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);

  useEffect(() => {
    fetchData();
    loadMapping();
    loadSummary();
    loadMissingValues();
  }, [projectId, currentPage]);
  
  useEffect(() => {
    if (showFillModal && selectedMissingFeature) {
      handleFillMissingValues();
    }
  }, [showFillModal, selectedMissingFeature]);

  const loadMapping = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/dataset/mapping`, {
        headers: {
          'Authorization': token || ''
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        setTargetLabel(result.targetLabel);
        setColumnMapping(result.columnMapping);
      }
    } catch (error) {
      console.error('Error loading mapping:', error);
    }
  };

  const loadSummary = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/dataset/summary`, {
        headers: {
          'Authorization': token || ''
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        setSummary(result);
      }
    } catch (error) {
      console.error('Error loading summary:', error);
    }
  };

  const loadMissingValues = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/dataset/missing-values`, {
        headers: {
          'Authorization': token || ''
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        setMissingValues(result);
      }
    } catch (error) {
      console.error('Error loading missing values:', error);
    }
  };

  useEffect(() => {
    if (data.length > 0) {
      const columns = Object.keys(data[0]).filter(key => key !== 'project_id' && key !== 'row_id');
      if (!targetLabel && columns.length > 0) {
        setTargetLabel(columns[0]);
      }
      // Initialize column mapping with default values
      if (Object.keys(columnMapping).length === 0) {
        const mapping: {[key: string]: string} = {};
        columns.forEach(col => {
          mapping[col] = columnMapping[col] || col.toLowerCase().replace(/ /g, '_');
        });
        setColumnMapping(mapping);
      }
    }
  }, [data]);

  const fetchData = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/dataset?page=${currentPage}`, {
        headers: {
          'Authorization': token || ''
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        setData(result.data || []);
        setTotalPages(result.totalPages || 1);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const handleUpload = async () => {
    if (!file) return;
    
    setUploading(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      // Read file as base64
      const fileContent = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]); // Remove data:... prefix
        };
        reader.readAsDataURL(file);
      });
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/dataset/upload`, {
        method: 'POST',
        headers: {
          'Authorization': token || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fileContent,
          fileName: file.name,
          replacementMode
        })
      });
      
      if (response.ok) {
        setShowUploadModal(false);
        setFile(null);
        setReplacementMode(false);
        fetchData();
        loadSummary();
        loadMissingValues();
      }
    } catch (error) {
      console.error('Error uploading file:', error);
    }
    setUploading(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      setFile(files[0]);
    }
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortColumn(null);
        setSortDirection(null);
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleMissingValueClick = async (featureName: string) => {
    if (selectedMissingFeature === featureName) {
      setSelectedMissingFeature(null);
      setFilteredData([]);
      return;
    }

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/dataset/missing-values?feature=${featureName}`, {
        headers: {
          'Authorization': token || ''
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        setFilteredData(result.missing_rows || []);
        setSelectedMissingFeature(featureName);
      }
    } catch (error) {
      console.error('Error loading missing value rows:', error);
    }
  };

  const getSortedData = () => {
    const dataToSort = selectedMissingFeature ? filteredData : data;
    if (!sortColumn || !sortDirection) return dataToSort;
    
    return [...dataToSort].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];
      
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      
      if (sortDirection === 'asc') {
        return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
      } else {
        return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
      }
    });
  };

  const saveMapping = async () => {
    setSaving(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      // Filter out empty mappings (columns to exclude)
      const activeMapping = Object.fromEntries(
        Object.entries(columnMapping).filter(([_, value]) => value.trim() !== '')
      );
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/dataset/mapping`, {
        method: 'POST',
        headers: {
          'Authorization': token || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          targetLabel,
          columnMapping: activeMapping
        })
      });
      
      if (response.ok) {
        setToast('Mapping saved successfully!');
        setTimeout(() => setToast(null), 5000);
      }
    } catch (error) {
      console.error('Error saving mapping:', error);
    }
    setSaving(false);
  };

  const getTargetDistribution = () => {
    if (!targetLabel || data.length === 0) return [];
    
    const counts: {[key: string]: number} = {};
    data.forEach(row => {
      const value = row[targetLabel];
      const key = value === null || value === undefined ? 'null' : String(value);
      counts[key] = (counts[key] || 0) + 1;
    });
    
    const maxCount = Math.max(...Object.values(counts));
    
    return Object.entries(counts)
      .map(([value, count]) => ({
        value,
        count,
        percentage: (count / maxCount) * 100
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10); // Show top 10 values
  };

  const renderTable = () => {
    const dataToRender = selectedMissingFeature ? filteredData : data;
    if (dataToRender.length === 0) {
      return <p>{selectedMissingFeature ? `No rows with missing ${selectedMissingFeature}` : 'No data available'}</p>;
    }
    
    const columns = Object.keys(dataToRender[0]).filter(key => key !== 'project_id' && key !== 'row_id');
    const sortedData = getSortedData();
    
    return (
      <div className="dataset-table">
        {selectedMissingFeature && (
          <div style={{ marginBottom: '1rem', padding: '0.5rem', backgroundColor: '#e3f2fd', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <strong>Filtered by missing values in: {selectedMissingFeature}</strong>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button 
                onClick={() => setShowFillModal(true)}
                style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem', backgroundColor: '#4caf50', color: 'white', border: 'none', borderRadius: '4px' }}
              >
                Fill Empty Values
              </button>
              <button 
                onClick={() => {
                  setSelectedMissingFeature(null);
                  setFilteredData([]);
                }}
                style={{ marginLeft: '1rem', padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
              >
                Clear Filter
              </button>
            </div>
          </div>
        )}
        <table>
          <thead>
            <tr>
              {columns.map(column => (
                <th key={column} onClick={() => handleSort(column)} className="sortable-header">
                  <span>{column}</span>
                  <span className="sort-indicator">
                    {sortColumn === column && sortDirection === 'asc' && ' ↑'}
                    {sortColumn === column && sortDirection === 'desc' && ' ↓'}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.map((row, index) => (
              <tr key={index}>
                {columns.map(column => (
                  <td key={column} style={{
                    backgroundColor: selectedMissingFeature === column && (row[column] === null || row[column] === undefined) ? '#ffebee' : 'transparent'
                  }}>
                    {row[column] === null || row[column] === undefined ? <em>null</em> : String(row[column])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const handleFillMissingValues = async () => {
    if (!selectedMissingFeature) return;
    
    setLoadingAnalysis(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      // First get analysis of the feature
      const analysisResponse = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/dataset/synthetic/analyze`, {
        method: 'POST',
        headers: {
          'Authorization': token || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      
      if (analysisResponse.ok) {
        const analysis = await analysisResponse.json();
        setFillAnalysis(analysis);
        
        // Set predefined values if it's categorical
        const featureAnalysis = analysis.field_analysis[selectedMissingFeature];
        if (featureAnalysis && featureAnalysis.type === 'categorical') {
          setPredefinedValues(featureAnalysis.unique_values || []);
          setIsPredefined(true);
        } else {
          setPredefinedValues([]);
          setIsPredefined(false);
        }
      }
    } catch (error) {
      console.error('Error analyzing feature:', error);
    }
    setLoadingAnalysis(false);
  };
  
  const generatePredictions = async () => {
    if (!selectedMissingFeature) return;
    
    setLoadingPredictions(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/dataset/fill-missing`, {
        method: 'POST',
        headers: {
          'Authorization': token || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          feature_name: selectedMissingFeature,
          predefined_values: predefinedValues,
          is_predefined: isPredefined
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        setPredictions(result.predictions.map((p: any) => ({ ...p, accepted: false, edited_value: p.predicted_value })));
      }
    } catch (error) {
      console.error('Error generating predictions:', error);
    }
    setLoadingPredictions(false);
  };
  
  const updateMissingValues = async () => {
    const acceptedPredictions = predictions.filter(p => p.accepted);
    if (acceptedPredictions.length === 0) return;
    
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const updates = acceptedPredictions.map(p => ({
        row_id: p.row_id,
        feature_name: selectedMissingFeature,
        value: p.edited_value
      }));
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/dataset/update-missing`, {
        method: 'POST',
        headers: {
          'Authorization': token || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ updates })
      });
      
      if (response.ok) {
        setShowFillModal(false);
        setPredictions([]);
        setSelectedMissingFeature(null);
        setFilteredData([]);
        fetchData();
        loadSummary();
        loadMissingValues();
        setToast('Missing values updated successfully!');
        setTimeout(() => setToast(null), 5000);
      }
    } catch (error) {
      console.error('Error updating missing values:', error);
    }
  };
  
  const renderFillModal = () => (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '800px', width: '90%' }}>
        <div className="modal-header">
          <h3>Fill Empty Values - {selectedMissingFeature}</h3>
          <button className="close-btn" onClick={() => { setShowFillModal(false); setPredictions([]); }}>×</button>
        </div>
        <div className="modal-body">
          {loadingAnalysis ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                border: '4px solid #f3f3f3', 
                borderTop: '4px solid #3498db', 
                borderRadius: '50%', 
                animation: 'spin 1s linear infinite',
                marginBottom: '1rem'
              }}></div>
              <p>Analyzing dataset...</p>
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          ) : !predictions.length ? (
            <>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={isPredefined}
                    onChange={(e) => setIsPredefined(e.target.checked)}
                  />
                  This column has predefined values
                </label>
              </div>
              
              {isPredefined && (
                <div style={{ marginBottom: '1rem' }}>
                  <h4>Predefined Values:</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    {predefinedValues.map((value, index) => (
                      <span key={index} style={{ padding: '0.25rem 0.5rem', backgroundColor: '#e3f2fd', borderRadius: '4px', fontSize: '0.9rem' }}>
                        {value}
                        <button
                          onClick={() => setPredefinedValues(predefinedValues.filter((_, i) => i !== index))}
                          style={{ marginLeft: '0.5rem', background: 'none', border: 'none', color: '#f44336', cursor: 'pointer' }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="text"
                      placeholder="Add new value"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                          setPredefinedValues([...predefinedValues, e.currentTarget.value.trim()]);
                          e.currentTarget.value = '';
                        }
                      }}
                      style={{ flex: 1, padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                    <button
                      onClick={(e) => {
                        const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                        if (input.value.trim()) {
                          setPredefinedValues([...predefinedValues, input.value.trim()]);
                          input.value = '';
                        }
                      }}
                      style={{ padding: '0.5rem', backgroundColor: '#4caf50', color: 'white', border: 'none', borderRadius: '50%', width: '40px', height: '40px' }}
                    >
                      +
                    </button>
                  </div>
                </div>
              )}
              
              <button
                onClick={generatePredictions}
                disabled={loadingPredictions}
                style={{ padding: '0.75rem 1.5rem', backgroundColor: '#2196f3', color: 'white', border: 'none', borderRadius: '4px' }}
              >
                {loadingPredictions ? 'Generating...' : 'Generate Predictions'}
              </button>
            </>
          ) : (
            <>
              <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h4>Predicted Values ({predictions.length} rows)</h4>
                <button
                  onClick={() => setPredictions(predictions.map(p => ({ ...p, accepted: true })))}
                  style={{ padding: '0.5rem 1rem', backgroundColor: '#4caf50', color: 'white', border: 'none', borderRadius: '4px' }}
                >
                  Accept All
                </button>
              </div>
              
              <div style={{ maxHeight: '400px', overflowY: 'auto', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 'max-content' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <th style={{ padding: '0.5rem', border: '1px solid #ddd', fontSize: '0.9rem', minWidth: '80px' }}>Row ID</th>
                      {predictions.length > 0 && Object.keys(predictions[0].row_data).map(key => (
                        <th key={key} style={{ padding: '0.5rem', border: '1px solid #ddd', fontSize: '0.9rem', minWidth: '100px' }}>{key}</th>
                      ))}
                      <th style={{ padding: '0.5rem', border: '1px solid #ddd', fontSize: '0.9rem', backgroundColor: '#e8f5e8', minWidth: '150px' }}>{selectedMissingFeature}</th>
                      <th style={{ padding: '0.5rem', border: '1px solid #ddd', fontSize: '0.9rem', minWidth: '80px' }}>Accept</th>
                    </tr>
                  </thead>
                  <tbody>
                    {predictions.map((prediction, index) => (
                      <tr key={index}>
                        <td style={{ padding: '0.5rem', border: '1px solid #ddd', fontSize: '0.8rem' }}>{prediction.row_id}</td>
                        {Object.entries(prediction.row_data).map(([key, value]) => (
                          <td key={key} style={{ padding: '0.5rem', border: '1px solid #ddd', fontSize: '0.8rem' }}>
                            {value === null || value === undefined ? <em>null</em> : String(value)}
                          </td>
                        ))}
                        <td style={{ padding: '0.25rem', border: '1px solid #ddd', backgroundColor: '#f9f9f9' }}>
                          <input
                            type="text"
                            value={prediction.edited_value}
                            onChange={(e) => {
                              const newPredictions = [...predictions];
                              newPredictions[index].edited_value = e.target.value;
                              setPredictions(newPredictions);
                            }}
                            style={{ 
                              width: '100%', 
                              padding: '0.25rem', 
                              border: 'none', 
                              outline: 'none',
                              backgroundColor: 'transparent',
                              fontSize: '0.8rem'
                            }}
                          />
                        </td>
                        <td style={{ padding: '0.5rem', border: '1px solid #ddd', textAlign: 'center' }}>
                          <button
                            onClick={() => {
                              const newPredictions = [...predictions];
                              newPredictions[index].accepted = !newPredictions[index].accepted;
                              setPredictions(newPredictions);
                            }}
                            style={{ 
                              padding: '0.25rem 0.5rem', 
                              backgroundColor: prediction.accepted ? '#4caf50' : '#f0f0f0', 
                              color: prediction.accepted ? 'white' : '#333', 
                              border: 'none', 
                              borderRadius: '4px',
                              minWidth: '30px'
                            }}
                          >
                            ✓
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        <div className="modal-actions">
          <button className="cancel-btn" onClick={() => { setShowFillModal(false); setPredictions([]); }}>Cancel</button>
          {predictions.length > 0 && (
            <button
              className="upload-btn-modal"
              onClick={updateMissingValues}
              disabled={!predictions.some(p => p.accepted)}
            >
              Update Dataset
            </button>
          )}
        </div>
      </div>
    </div>
  );
  
  const renderUploadModal = () => (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>Upload Dataset</h3>
          <button className="close-btn" onClick={() => setShowUploadModal(false)}>×</button>
        </div>
        <div className="modal-body">
          <div className="file-upload-area">
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              id="file-input"
              className="file-input"
            />
            <label 
              htmlFor="file-input" 
              className={`file-label ${dragOver ? 'drag-over' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="upload-icon">📁</div>
              <div className="upload-text">
                {file ? file.name : 'Drag file to upload or click to browse file'}
              </div>
              <div className="upload-subtext">Allowed formats: CSV, XLSX</div>
            </label>
          </div>
          <div className="checkbox-container">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={replacementMode}
                onChange={(e) => setReplacementMode(e.target.checked)}
                className="checkbox"
              />
              <span className="checkmark"></span>
              <span className="checkbox-text">
                Replacement Mode
                <small>Remove all existing data and replace with new upload</small>
              </span>
            </label>
          </div>
        </div>
        <div className="modal-actions">
          <button className="cancel-btn" onClick={() => setShowUploadModal(false)}>Cancel</button>
          <button className="upload-btn-modal" onClick={handleUpload} disabled={!file || uploading}>
            {uploading ? 'Uploading...' : 'Upload Dataset'}
          </button>
        </div>
      </div>
    </div>
  );

  const isTestDataset = window.location.pathname.includes('/test/dataset');
  
  return (
    <div className="module-content">
      <Breadcrumb items={[
        { label: 'Home', path: '/' },
        { label: 'Projects', path: '/' },
        { label: projectId, path: `/project/${projectId}` },
        ...(isTestDataset ? [
          { label: 'Test', path: `/project/${projectId}/test` },
          { label: 'Dataset' }
        ] : [
          { label: 'Dataset' }
        ])
      ]} />
      <div className="dataset-header">
        <h2>Dataset Management</h2>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button className="upload-btn" onClick={() => setShowUploadModal(true)}>
            Upload Data
          </button>
          <button 
            className="synthetic-btn"
            onClick={() => window.location.href = `/project/${projectId}/test/dataset/synthetic`}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '1rem',
              cursor: 'pointer'
            }}
          >
            🧬 Generate Synthetic Dataset
          </button>
        </div>
      </div>
      
      {loading ? (
        <p>Loading...</p>
      ) : data.length > 0 ? (
        <>
          <div className="data-visualization">
            <h3>Data Visualization</h3>
            <div className="viz-stats">
              <div className="stat-card">
                <div className="stat-number">{summary?.total_rows || data.length}</div>
                <div className="stat-label">Total Rows</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">{summary?.total_columns || Object.keys(data[0] || {}).filter(key => key !== 'project_id' && key !== 'row_id').length}</div>
                <div className="stat-label">Columns</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">{summary ? Object.keys(summary.column_stats || {}).length : Object.keys(data[0] || {}).filter(key => key !== 'project_id' && key !== 'row_id').filter(col => typeof data[0][col] === 'number').length}</div>
                <div className="stat-label">Features</div>
              </div>
            </div>
            
            {targetLabel && summary?.column_stats?.[targetLabel] && (
              <div className="target-distribution">
                <h4>Target Label Distribution: {targetLabel}</h4>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={Object.entries(summary.column_stats[targetLabel])
                      .sort(([,a], [,b]) => (b as number) - (a as number))
                      .slice(0, 10)
                      .map(([value, count]) => ({
                        name: String(value),
                        count: count as number
                      }))
                    }
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                  >
                    <XAxis 
                      dataKey="name" 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      interval={0}
                    />
                    <YAxis 
                      domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.2)]}
                      label={{ value: 'Count', angle: -90, position: 'insideLeft' }}
                      allowDecimals={false}
                      tickCount={6}
                    />
                    <Tooltip />
                    <Bar dataKey="count" fill="#4299e1">
                      <LabelList dataKey="count" position="top" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            
            {missingValues && Object.keys(missingValues).length > 0 && (
              <div className="missing-values-dashboard">
                <h4>Missing Values by Feature</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={Object.entries(missingValues)
                      .sort(([,a], [,b]) => (b as number) - (a as number))
                      .map(([feature, count]) => ({
                        name: feature,
                        count: count as number
                      }))
                    }
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                  >
                    <XAxis 
                      dataKey="name" 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      interval={0}
                    />
                    <YAxis 
                      domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.2)]}
                      label={{ value: 'Missing Count', angle: -90, position: 'insideLeft' }}
                      allowDecimals={false}
                      tickCount={6}
                    />
                    <Tooltip />
                    <Bar 
                      dataKey="count" 
                      fill="#f44336"
                      onClick={(data) => data.name && handleMissingValueClick(data.name)}
                      style={{ cursor: 'pointer' }}
                    >
                      <LabelList dataKey="count" position="top" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                  Click on bars to filter data showing only rows with missing values for that feature
                </p>
              </div>
            )}
          </div>
          
          <div className="dataset-controls">
            <div className="control-group">
              <label>Target Label:</label>
              <select 
                value={targetLabel} 
                onChange={(e) => setTargetLabel(e.target.value)}
                className="target-dropdown"
              >
                {(summary ? Object.keys(summary.column_stats || {}) : Object.keys(data[0] || {}).filter(key => key !== 'project_id' && key !== 'row_id')).map(column => (
                  <option key={column} value={column}>{column}</option>
                ))}
              </select>
            </div>
            <button 
              className="mapping-btn" 
              onClick={() => setShowMapping(!showMapping)}
            >
              {showMapping ? 'Hide' : 'Show'} Feature Mapping
            </button>
          </div>
          
          {showMapping && (
            <div className="column-mapping">
              <h4>Feature Mapping</h4>
              <div className="mapping-grid">
                {(summary ? Object.keys(summary.column_stats || {}) : Object.keys(data[0] || {}).filter(key => key !== 'project_id' && key !== 'row_id')).map(column => (
                  <div key={column} className="mapping-row">
                    <label>{column}:</label>
                    <input
                      type="text"
                      value={columnMapping[column] || column.toLowerCase().replace(/ /g, '_')}
                      onChange={(e) => setColumnMapping({...columnMapping, [column]: e.target.value})}
                      placeholder="Feature name (leave blank to exclude)"
                    />
                  </div>
                ))}
              </div>
              <button 
                className="save-mapping-btn" 
                onClick={saveMapping}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Mapping'}
              </button>
            </div>
          )}
          
          {renderTable()}
          <div className="pagination">
            <button 
              disabled={currentPage === 1} 
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              Previous
            </button>
            <span>Page {currentPage} of {totalPages}</span>
            <button 
              disabled={currentPage === totalPages} 
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </>
      ) : (
        <p>No data available</p>
      )}
      
      {showUploadModal && renderUploadModal()}
      
      {toast && (
        <div className="toast">
          {toast}
        </div>
      )}
      
      {showFillModal && renderFillModal()}
    </div>
  );
};

export default DatasetManagement;